import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { UserDirectoryService } from 'src/app/services/user-directory.service';
import { UserSimulationService } from 'src/app/services/user-simulation.service';

@Component({
  selector: 'app-user-simulation',
  imports: [TranslatePipe],
  templateUrl: './user-simulation.component.html',
  styleUrl: './user-simulation.component.scss',
})
export class UserSimulationComponent {
  readonly simulation = inject(UserSimulationService);
  private readonly users = inject(UserDirectoryService);
  private readonly contractors = inject(ContractorsService);
  private readonly navigation = inject(NavigationService);
  readonly query = signal('');
  readonly directory = toSignal(toObservable(this.simulation.available).pipe(
    switchMap((available) => available ? combineLatest([this.users.getListFromDb(), this.contractors.getList()]).pipe(
      map(([users, contractors]) => ({
        loading: false, error: false,
        users: users.map((user) => ({
          guid: user.guid,
          label: [
            user.displayName, user.role,
            ...(user.contractors ?? []).map((guid) => contractors.find((item) => item.guid === guid)?.name ?? guid),
            user.company,
          ].filter(Boolean).join(' · '),
        })),
      })),
      catchError(() => of({ loading: false, error: true, users: [] })),
    ) : of({ loading: false, error: false, users: [] })),
  ), { initialValue: { loading: true, error: false, users: [] } });

  readonly filteredUsers = computed(() => this.directory().users.filter((user) =>
    user.label.toLocaleLowerCase().includes(this.query().toLocaleLowerCase())));

  constructor() {
    let previous = this.simulation.activeUser()?.guid ?? null;
    effect(() => {
      const current = this.simulation.activeUser()?.guid ?? null;
      if (current !== previous) {
        previous = current;
        untracked(() => void this.navigation.navigateToHome());
      }
    });
  }
}
